import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';

import {
    GetAdminData,
    UpdateAdminConfig,
    UpdateAdminOverlay,
    UploadAsset,
    GetThemes
} from '../../../../utilities/DL/PersonalQuickReaction';
import { resolveAssetUrl } from '../../../../utilities/BL/PersonalQuickReaction';
import SignalRService from '../../../../utilities/WS/SignalRService';

import { QuickReactionPersonalMetricsRow } from './QuickReactionPersonalMetricsRow';
import { QuickReactionPersonalThemeModal } from './QuickReactionPersonalThemeModal';
import { QuickReactionPersonalResetModal } from './QuickReactionPersonalResetModal';
import { QuickReactionPersonalDangerModal } from './QuickReactionPersonalDangerModal';

/**
 * COMPONENTE: QuickReactionPersonalBoard
 * [HU-06] Consola Administrativa Touch-Friendly para Reacción Rápida Personal
 *
 * Ofrece:
 * - Monitoreo de 10 KPIs de telemetría y conversión en vivo.
 * - Conexión SignalR bidireccional en tiempo real con reconexión automática.
 * - 4 Acordeones de gestión:
 *   1. Control de Live Overlay (OBS / broadcast, 1 o 2 QRs, banners 1536/1920, offset Y).
 *   2. Parámetros del Juego y Anti-Spam (Estrategias, Modo Estricto, Delays, Umbrales).
 *   3. Gestor de Temas Visuales (Crear, editar y activar temas y sprites).
 *   4. Zona Peligrosa (Reinicio total maestro protegido por clave).
 * - Tabla táctil de participantes con buscador, filtro de estado, protección de PII,
 *   reintentos individuales ("Dar otra oportunidad") y exportación a Excel multi-hoja (.xlsx).
 */
export const QuickReactionPersonalBoard = ({
    getOutOfTheDynamic = () => {},
    roomId: propRoomId = null
}) => {
    // -------------------------------------------------------------------------
    // 1. RESOLUCIÓN DE IDENTIFICADOR DE SALA (ROOM ID)
    // -------------------------------------------------------------------------
    const initialRoomId = useMemo(() => {
        if (propRoomId) return propRoomId;
        if (typeof window !== 'undefined') {
            const searchParams = new URLSearchParams(window.location.search);
            const urlRoomId = searchParams.get('roomId') || searchParams.get('room');
            if (urlRoomId) return urlRoomId;
            const storedRoomId = localStorage.getItem('active_room_id') || localStorage.getItem('interaktia_room_id') || localStorage.getItem('roomId') || localStorage.getItem('room');
            if (storedRoomId) return storedRoomId;
        }
        return '';
    }, [propRoomId]);

    const [resolvedRoomId, setResolvedRoomId] = useState('');
    const roomId = propRoomId || resolvedRoomId || initialRoomId || '';

    // -------------------------------------------------------------------------
    // 2. ESTADOS DE DATOS CONSOLIDADOS
    // -------------------------------------------------------------------------
    const [isLoading, setIsLoading] = useState(true);
    const [wsStatus, setWsStatus] = useState('connecting'); // 'connecting', 'connected', 'reconnecting', 'error', 'closed'
    const [alertNotification, setAlertNotification] = useState(null);

    // Entidades principales
    const [config, setConfig] = useState(null);
    const [themes, setThemes] = useState([]);
    const [activeTheme, setActiveTheme] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [ranking, setRanking] = useState([]);
    const [stats, setStats] = useState(null);

    // -------------------------------------------------------------------------
    // 3. ESTADOS DE CONTROL DE ACORDEONES
    // -------------------------------------------------------------------------
    const [openAccordion, setOpenAccordion] = useState('overlay'); // 'overlay' | 'game' | 'themes' | 'danger' | null

    const toggleAccordion = (sectionName) => {
        setOpenAccordion((prev) => (prev === sectionName ? null : sectionName));
    };

    // -------------------------------------------------------------------------
    // 4. ESTADOS LOCALES DE FORMULARIO PARA ACORDEONES
    // -------------------------------------------------------------------------
    // a) Live Overlay
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayMode, setOverlayMode] = useState('invitation'); // 'invitation' | 'winner'
    const [overlayQrCount, setOverlayQrCount] = useState(1);
    const [overlayOffsetY, setOverlayOffsetY] = useState(0);
    const [overlayBanner1536Url, setOverlayBanner1536Url] = useState('');
    const [overlayBanner1920Url, setOverlayBanner1920Url] = useState('');
    const [isSavingOverlay, setIsSavingOverlay] = useState(false);
    const [uploadingBannerType, setUploadingBannerType] = useState(null);

    // b) Parámetros del Juego
    const [gameIsActive, setGameIsActive] = useState(true);
    const [earlyFaultStrategy, setEarlyFaultStrategy] = useState('NONE'); // NONE, THROTTLE, RESET_DELAY, TIME_PENALTY
    const [allowEarlyFaultDetection, setAllowEarlyFaultDetection] = useState(false);
    const [minWaitDelayMs, setMinWaitDelayMs] = useState(3000);
    const [maxWaitDelayMs, setMaxWaitDelayMs] = useState(6000);
    const [inactivityTimeoutSeconds, setInactivityTimeoutSeconds] = useState(7);
    const [minHumanReactionThresholdMs, setMinHumanReactionThresholdMs] = useState(120);
    const [allowSharedDevice, setAllowSharedDevice] = useState(true);
    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // c) Gestor de Temas
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [isActivatingTheme, setIsActivatingTheme] = useState(false);

    // -------------------------------------------------------------------------
    // 5. ESTADOS DE TABLA DE PARTICIPANTES (BÚSQUEDA, FILTRO, PII)
    // -------------------------------------------------------------------------
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'PLAYED' | 'WINNER' | 'FAULT' | 'PENDING' | 'RESET'
    const [showPII, setShowPII] = useState(false);
    const [isExportingExcel, setIsExportingExcel] = useState(false);

    // -------------------------------------------------------------------------
    // 6. ESTADOS DE MODALES
    // -------------------------------------------------------------------------
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [editingTheme, setEditingTheme] = useState(null);

    const [showResetModal, setShowResetModal] = useState(false);
    const [resetTargetParticipant, setResetTargetParticipant] = useState(null);

    const [showDangerModal, setShowDangerModal] = useState(false);

    // Helper para mostrar notificaciones flotantes / alertas
    const triggerToast = useCallback((type, message) => {
        setAlertNotification({ type, message });
        setTimeout(() => {
            setAlertNotification(null);
        }, 4500);
    }, []);

    // -------------------------------------------------------------------------
    // 7. CARGA INICIAL CONSOLIDADA (REST GET DATA)
    // -------------------------------------------------------------------------
    const loadAdminData = useCallback(async (isSilent = false) => {
        if (!isSilent) setIsLoading(true);

        try {
            const response = await GetAdminData(roomId || null);
            if (response && response.success && response.data) {
                const data = response.data;

                // Capturar el roomId efectivo retornado por el backend si no estaba establecido
                if (data.config?.roomId) {
                    setResolvedRoomId((prev) => prev || data.config.roomId);
                }

                // 1. Configuración y Overlay
                if (data.config) {
                    setConfig(data.config);
                    setGameIsActive(data.config.isActive !== undefined ? Boolean(data.config.isActive) : true);
                    setEarlyFaultStrategy(data.config.earlyFaultStrategy || 'NONE');
                    setAllowEarlyFaultDetection(Boolean(data.config.allowEarlyFaultDetection));
                    setMinWaitDelayMs(data.config.minWaitDelayMs || 3000);
                    setMaxWaitDelayMs(data.config.maxWaitDelayMs || 6000);
                    setInactivityTimeoutSeconds(data.config.inactivityTimeoutSeconds || 7);
                    setMinHumanReactionThresholdMs(data.config.minHumanReactionThresholdMs || 120);
                    setAllowSharedDevice(data.config.allowSharedDevice !== undefined ? Boolean(data.config.allowSharedDevice) : true);

                    setOverlayVisible(Boolean(data.config.overlayVisible));
                    setOverlayMode(data.config.overlayMode || 'invitation');
                    setOverlayQrCount(Number(data.config.overlayQrCount) || 1);
                    setOverlayOffsetY(Number(data.config.overlayOffsetY) || 0);
                    setOverlayBanner1536Url(data.config.overlayBanner1536Url || '');
                    setOverlayBanner1920Url(data.config.overlayBanner1920Url || '');

                    if (data.config.activeThemeId) {
                        setSelectedThemeId((prev) => prev || data.config.activeThemeId);
                    }
                }

                // 2. Temas
                if (Array.isArray(data.themes)) {
                    setThemes(data.themes);
                }
                if (data.activeTheme) {
                    setActiveTheme(data.activeTheme);
                    if (data.activeTheme.id) {
                        setSelectedThemeId((prev) => prev || data.activeTheme.id);
                    }
                }

                // 3. Participantes y Ranking
                if (Array.isArray(data.participants)) {
                    setParticipants(data.participants);
                }
                if (data.ranking && Array.isArray(data.ranking.ranking)) {
                    setRanking(data.ranking.ranking);
                }

                // 4. Estadísticas
                if (data.stats) {
                    setStats(data.stats);
                }

                if (!isSilent) {
                    triggerToast('success', 'Datos administrativos actualizados.');
                }
            } else {
                if (!isSilent) {
                    triggerToast('danger', response?.message || 'Error al obtener datos administrativos.');
                }
            }
        } catch (error) {
            console.error('[QuickReactionPersonalBoard] Error al cargar datos:', error);
            if (!isSilent) {
                triggerToast('danger', 'Error de red al consultar el panel administrativo.');
            }
        } finally {
            if (!isSilent) setIsLoading(false);
        }
    }, [roomId, triggerToast]);

    const loadAdminDataRef = useRef(loadAdminData);
    useEffect(() => {
        loadAdminDataRef.current = loadAdminData;
    }, [loadAdminData]);

    // -------------------------------------------------------------------------
    // 8. CONEXIÓN EN TIEMPO REAL CON SIGNALR
    // -------------------------------------------------------------------------
    useEffect(() => {
        let isMounted = true;

        loadAdminDataRef.current(false);

        const connectWs = async () => {
            try {
                setWsStatus('connecting');
                await SignalRService.connect({ roomId, tipo: 'admin' });
                if (isMounted) setWsStatus('connected');
            } catch (err) {
                console.error('[QuickReactionPersonalBoard] Error conectando a SignalR:', err);
                if (isMounted) setWsStatus('error');
            }
        };

        connectWs();

        // Handlers en tiempo real
        const handleNewPlay = (payload) => {
            const playData = payload && (payload.data !== undefined ? payload.data : payload);
            if (!playData) return;

            console.log('[QuickReactionPersonalBoard] WS: newPlay recibido:', playData);

            // Actualizar o insertar participante en la lista
            setParticipants((prev) => {
                const participantId = playData.participantId;
                const exists = prev.some((p) => p.id === participantId);

                if (exists) {
                    return prev.map((p) => {
                        if (p.id === participantId) {
                            return {
                                ...p,
                                reactionTimeMs: playData.reactionTimeMs,
                                penaltyTimeMs: playData.penaltyTimeMs || 0,
                                totalTimeMs: playData.totalTimeMs || (playData.reactionTimeMs + (playData.penaltyTimeMs || 0)),
                                isEarlyFault: Boolean(playData.isEarlyFault),
                                isWinner: Boolean(playData.isWinner),
                                badgeName: playData.badgeName || p.badgeName,
                                outcomeStatus: playData.outcomeStatus || (playData.isEarlyFault ? 'DISQUALIFIED' : 'COMPLETED'),
                                playedAtUtc: playData.playedAtUtc || new Date().toISOString()
                            };
                        }
                        return p;
                    });
                }

                // Si no existía, agregarlo
                const newParticipant = {
                    id: participantId,
                    roomId: roomId,
                    participantName: playData.participantName || 'Aficionado',
                    reactionTimeMs: playData.reactionTimeMs,
                    penaltyTimeMs: playData.penaltyTimeMs || 0,
                    totalTimeMs: playData.totalTimeMs || (playData.reactionTimeMs + (playData.penaltyTimeMs || 0)),
                    isEarlyFault: Boolean(playData.isEarlyFault),
                    isWinner: Boolean(playData.isWinner),
                    badgeName: playData.badgeName || '',
                    outcomeStatus: playData.outcomeStatus || 'COMPLETED',
                    playedAtUtc: playData.playedAtUtc || new Date().toISOString(),
                    registeredAtUtc: new Date().toISOString()
                };

                return [newParticipant, ...prev];
            });

            // Actualizar Stats reactivamente
            setStats((prev) => {
                if (!prev) return prev;
                const totalPlayed = (prev.totalPlayed || 0) + 1;
                const isFault = Boolean(playData.isEarlyFault);
                const totalDisqualified = isFault ? (prev.totalDisqualified || 0) + 1 : (prev.totalDisqualified || 0);

                const validTime = !isFault && playData.reactionTimeMs > 0 ? playData.reactionTimeMs : null;
                let bestTime = prev.bestReactionTimeMs || 0;
                if (validTime && (bestTime === 0 || validTime < bestTime)) {
                    bestTime = validTime;
                }

                return {
                    ...prev,
                    totalPlayed,
                    totalDisqualified,
                    bestReactionTimeMs: bestTime
                };
            });

            triggerToast('info', `⚡ Nueva jugada de ${playData.participantName || 'un aficionado'}: ${playData.reactionTimeMs || 0} ms`);
        };

        const handleConfigUpdated = (payload) => {
            const configData = payload && (payload.data !== undefined ? payload.data : payload);
            if (!configData) return;
            console.log('[QuickReactionPersonalBoard] WS: configUpdated:', configData);

            setConfig(configData);
            if (configData.isActive !== undefined) setGameIsActive(Boolean(configData.isActive));
            if (configData.earlyFaultStrategy) setEarlyFaultStrategy(configData.earlyFaultStrategy);
            if (configData.allowEarlyFaultDetection !== undefined) setAllowEarlyFaultDetection(Boolean(configData.allowEarlyFaultDetection));
            if (configData.activeTheme) setActiveTheme(configData.activeTheme);
        };

        const handleOverlayUpdated = (payload) => {
            const overlayData = payload && (payload.data !== undefined ? payload.data : payload);
            if (!overlayData) return;
            console.log('[QuickReactionPersonalBoard] WS: overlayUpdated:', overlayData);

            if (overlayData.overlayVisible !== undefined) setOverlayVisible(Boolean(overlayData.overlayVisible));
            if (overlayData.overlayMode) setOverlayMode(overlayData.overlayMode);
            if (overlayData.overlayQrCount !== undefined) setOverlayQrCount(Number(overlayData.overlayQrCount));
            if (overlayData.overlayOffsetY !== undefined) setOverlayOffsetY(Number(overlayData.overlayOffsetY));
        };

        const handleReset = (payload) => {
            const resetData = payload && (payload.data !== undefined ? payload.data : payload);
            console.log('[QuickReactionPersonalBoard] WS: reset recibido:', resetData);
            loadAdminDataRef.current(true);
        };

        const handleReconnected = () => {
            if (isMounted) {
                setWsStatus('connected');
                loadAdminDataRef.current(true);
            }
        };

        const handleReconnecting = () => {
            if (isMounted) setWsStatus('reconnecting');
        };

        const handleClose = () => {
            if (isMounted) setWsStatus('closed');
        };

        // Suscripciones
        SignalRService.on('personalQuickReaction:newPlay', handleNewPlay);
        SignalRService.on('personalQuickReaction:configUpdated', handleConfigUpdated);
        SignalRService.on('personalQuickReaction:overlayUpdated', handleOverlayUpdated);
        SignalRService.on('personalQuickReaction:reset', handleReset);
        SignalRService.on('reconnected', handleReconnected);
        SignalRService.on('reconnecting', handleReconnecting);
        SignalRService.on('close', handleClose);

        return () => {
            isMounted = false;
            SignalRService.off('personalQuickReaction:newPlay', handleNewPlay);
            SignalRService.off('personalQuickReaction:configUpdated', handleConfigUpdated);
            SignalRService.off('personalQuickReaction:overlayUpdated', handleOverlayUpdated);
            SignalRService.off('personalQuickReaction:reset', handleReset);
            SignalRService.off('reconnected', handleReconnected);
            SignalRService.off('reconnecting', handleReconnecting);
            SignalRService.off('close', handleClose);
            SignalRService.disconnect();
        };
    }, [roomId]);

    // -------------------------------------------------------------------------
    // 9. ACCIÓN: GUARDAR OVERLAY
    // -------------------------------------------------------------------------
    const handleSaveOverlay = async (e) => {
        if (e) e.preventDefault();
        setIsSavingOverlay(true);
        try {
            const payload = {
                roomId,
                overlayVisible,
                overlayMode,
                overlayQrCount,
                overlayOffsetY,
                overlayBanner1536Url: overlayBanner1536Url || null,
                overlayBanner1920Url: overlayBanner1920Url || null
            };

            const response = await UpdateAdminOverlay(payload);
            if (response && response.success) {
                triggerToast('success', 'Control de Live Overlay actualizado.');
            } else {
                triggerToast('danger', response?.message || 'Error al actualizar el Overlay.');
            }
        } catch (error) {
            console.error('[QuickReactionPersonalBoard] Error al guardar overlay:', error);
            triggerToast('danger', 'Error de red al actualizar el overlay.');
        } finally {
            setIsSavingOverlay(false);
        }
    };

    // Subida de cintillos de overlay
    const handleUploadOverlayBanner = async (file, bannerType) => {
        if (!file) return;
        setUploadingBannerType(bannerType);
        try {
            const result = await UploadAsset(file, bannerType, roomId);
            if (result && result.success && result.url) {
                if (bannerType === 'banner_1536') {
                    setOverlayBanner1536Url(result.url);
                } else {
                    setOverlayBanner1920Url(result.url);
                }
                triggerToast('success', `Cintillo ${bannerType === 'banner_1536' ? '1536px' : '1920px'} subido.`);
            } else {
                triggerToast('danger', result?.message || 'Error al subir el cintillo.');
            }
        } catch (err) {
            console.error('[QuickReactionPersonalBoard] Error al subir cintillo:', err);
            triggerToast('danger', 'Error de red al subir el cintillo.');
        } finally {
            setUploadingBannerType(null);
        }
    };

    // -------------------------------------------------------------------------
    // 10. ACCIÓN: GUARDAR PARÁMETROS DEL JUEGO Y ANTI-SPAM
    // -------------------------------------------------------------------------
    const handleSaveConfig = async (e) => {
        if (e) e.preventDefault();
        setIsSavingConfig(true);
        try {
            const payload = {
                roomId,
                isActive: gameIsActive,
                earlyFaultStrategy,
                allowEarlyFaultDetection,
                minWaitDelayMs: Number(minWaitDelayMs),
                maxWaitDelayMs: Number(maxWaitDelayMs),
                inactivityTimeoutSeconds: Number(inactivityTimeoutSeconds),
                minHumanReactionThresholdMs: Number(minHumanReactionThresholdMs),
                allowSharedDevice,
                activeThemeId: activeTheme?.id || null
            };

            const response = await UpdateAdminConfig(payload);
            if (response && response.success) {
                triggerToast('success', 'Parámetros del juego y Anti-Spam guardados.');
            } else {
                triggerToast('danger', response?.message || 'Error al guardar los parámetros.');
            }
        } catch (error) {
            console.error('[QuickReactionPersonalBoard] Error al guardar config:', error);
            triggerToast('danger', 'Error de red al guardar la configuración.');
        } finally {
            setIsSavingConfig(false);
        }
    };

    // -------------------------------------------------------------------------
    // 11. ACCIÓN: ACTIVAR TEMA SELECCIONADO
    // -------------------------------------------------------------------------
    const handleActivateTheme = async () => {
        if (!selectedThemeId) {
            triggerToast('warning', 'Selecciona un tema para activar.');
            return;
        }

        setIsActivatingTheme(true);
        try {
            const payload = {
                roomId,
                isActive: gameIsActive,
                earlyFaultStrategy,
                allowEarlyFaultDetection,
                minWaitDelayMs: Number(minWaitDelayMs),
                maxWaitDelayMs: Number(maxWaitDelayMs),
                inactivityTimeoutSeconds: Number(inactivityTimeoutSeconds),
                minHumanReactionThresholdMs: Number(minHumanReactionThresholdMs),
                allowSharedDevice,
                activeThemeId: selectedThemeId,
                overlayVisible,
                overlayMode,
                overlayQrCount: Number(overlayQrCount),
                overlayOffsetY: Number(overlayOffsetY)
            };

            const response = await UpdateAdminConfig(payload);
            if (response && response.success) {
                const newlyActive = themes.find((t) => t.id === selectedThemeId);
                if (newlyActive) setActiveTheme(newlyActive);
                triggerToast('success', 'Tema activo actualizado.');
            } else {
                triggerToast('danger', response?.message || 'Error al activar el tema.');
            }
        } catch (error) {
            console.error('[QuickReactionPersonalBoard] Error al activar tema:', error);
            triggerToast('danger', 'Error de red al cambiar tema activo.');
        } finally {
            setIsActivatingTheme(false);
        }
    };

    // -------------------------------------------------------------------------
    // 12. MÁSCARAS DE SEGURIDAD PARA PII
    // -------------------------------------------------------------------------
    const maskEmail = (email) => {
        if (!email) return '—';
        if (showPII) return email;
        const parts = email.split('@');
        if (parts.length !== 2) return '***@***';
        const namePart = parts[0];
        const domain = parts[1];
        const maskedName = namePart.length > 2
            ? `${namePart.slice(0, 1)}***${namePart.slice(-1)}`
            : '***';
        return `${maskedName}@${domain}`;
    };

    const maskPhone = (phone) => {
        if (!phone) return '—';
        if (showPII) return phone;
        const clean = phone.replace(/\s+/g, '');
        if (clean.length > 4) {
            return `***-***-${clean.slice(-4)}`;
        }
        return '***-***';
    };

    // -------------------------------------------------------------------------
    // 13. FILTRADO Y BÚSQUEDA DE PARTICIPANTES
    // -------------------------------------------------------------------------
    const filteredParticipants = useMemo(() => {
        return participants.filter((p) => {
            // Filtro por término de búsqueda (nombre, email, teléfono)
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase().trim();
                const nameMatch = (p.participantName || '').toLowerCase().includes(term);
                const emailMatch = (p.email || '').toLowerCase().includes(term);
                const phoneMatch = (p.phone || '').includes(term);
                if (!nameMatch && !emailMatch && !phoneMatch) return false;
            }

            // Filtro por Estado
            switch (statusFilter) {
                case 'PLAYED':
                    return p.playedAtUtc || p.reactionTimeMs > 0;
                case 'WINNER':
                    return Boolean(p.isWinner);
                case 'FAULT':
                    return Boolean(p.isEarlyFault) || p.outcomeStatus === 'DISQUALIFIED';
                case 'PENDING':
                    return !p.playedAtUtc && !p.reactionTimeMs;
                case 'RESET':
                    return Boolean(p.resetByAdmin) || Boolean(p.isArchived);
                case 'ALL':
                default:
                    return true;
            }
        });
    }, [participants, searchTerm, statusFilter]);

    // -------------------------------------------------------------------------
    // 14. EXPORTACIÓN A EXCEL MULTI-HOJA (.xlsx) CON SheetJS
    // -------------------------------------------------------------------------
    const handleExportExcel = () => {
        setIsExportingExcel(true);
        try {
            const cleanRoom = (roomId || 'Sala').replace(/[^a-zA-Z0-9_-]/g, '_');
            const todayStr = new Date().toISOString().slice(0, 10);

            // --- HOJA 1: RANKING OFICIAL ---
            const rankingList = [...participants]
                .filter((p) => p.reactionTimeMs > 0 && !p.isEarlyFault)
                .sort((a, b) => (a.totalTimeMs || a.reactionTimeMs) - (b.totalTimeMs || b.reactionTimeMs));

            const sheet1Data = rankingList.length > 0 ? rankingList.map((p, idx) => ({
                'Posición': idx + 1,
                'Participante': p.participantName || 'Sin Nombre',
                'Tiempo de Reacción (ms)': p.reactionTimeMs || 0,
                'Penalización (ms)': p.penaltyTimeMs || 0,
                'Tiempo Total (ms)': p.totalTimeMs || (p.reactionTimeMs + (p.penaltyTimeMs || 0)),
                'Faltas Tempranas': p.earlyAttemptsCount || 0,
                'Insignia Oficial': p.badgeName || 'Sin Insignia',
                '¿Es Ganador?': p.isWinner ? 'SÍ' : 'NO',
                'Fecha de Jugada': p.playedAtUtc ? new Date(p.playedAtUtc).toLocaleString('es-MX') : '—'
            })) : [{ 'Mensaje': 'No hay participantes clasificados con jugadas válidas aún.' }];

            // --- HOJA 2: PARTICIPANTES TOTALES ---
            const sheet2Data = participants.length > 0 ? participants.map((p, idx) => ({
                '#': idx + 1,
                'ID Participante': p.id || '—',
                'Nombre Completo': p.participantName || '—',
                'Correo Electrónico': p.email || '—',
                'Teléfono': p.phone || '—',
                'Términos Aceptados': p.acceptedTerms ? 'SÍ' : 'NO',
                'Privacidad Aceptada': p.acceptedPrivacy ? 'SÍ' : 'NO',
                'Estado': p.outcomeStatus || (p.isEarlyFault ? 'DESCALIFICADO' : (p.playedAtUtc ? 'JUGADO' : 'PENDIENTE')),
                'Token Consumido': p.isTokenConsumed ? 'SÍ' : 'NO',
                'Tiempo Reacción (ms)': p.reactionTimeMs || 0,
                'Penalización (ms)': p.penaltyTimeMs || 0,
                'Tiempo Total (ms)': p.totalTimeMs || (p.reactionTimeMs ? (p.reactionTimeMs + (p.penaltyTimeMs || 0)) : 0),
                'Faltas Tempranas': p.earlyAttemptsCount || 0,
                'Insignia': p.badgeName || '—',
                '¿Ganador?': p.isWinner ? 'SÍ' : 'NO',
                'Fecha Registro': p.registeredAtUtc ? new Date(p.registeredAtUtc).toLocaleString('es-MX') : '—',
                'Fecha Jugada': p.playedAtUtc ? new Date(p.playedAtUtc).toLocaleString('es-MX') : '—',
                '¿Segunda Oportunidad?': p.resetByAdmin ? 'SÍ' : 'NO'
            })) : [{ 'Mensaje': 'Sin registros de participantes.' }];

            // --- HOJA 3: TELEMETRÍA Y CONVERSIÓN ---
            const telemetry = stats?.telemetryStepCounts || {};
            const totalReg = stats?.totalRegistered || participants.length;
            const totalPlayed = stats?.totalPlayed || 0;
            const convRate = totalReg > 0 ? `${((totalPlayed / totalReg) * 100).toFixed(1)}%` : '0.0%';

            const sheet3Data = [
                { 'Métrica / Hito del Embudo': '=== HITOS DE TELEMETRÍA (10 KPIs) ===', 'Valor': '', 'Detalle': '' },
                { 'Métrica / Hito del Embudo': '1. Escaneos QR (qr_scan)', 'Valor': telemetry.qr_scan ?? 0, 'Detalle': 'Lecturas de código QR en pantallas y publicidad' },
                { 'Métrica / Hito del Embudo': '2. Vistas de Página (page_view)', 'Valor': telemetry.page_view ?? 0, 'Detalle': 'Visitas a la pantalla móvil de juego' },
                { 'Métrica / Hito del Embudo': '3. Inicios Formulario (form_start)', 'Valor': telemetry.form_start ?? 0, 'Detalle': 'Aficionados que comenzaron a llenar datos' },
                { 'Métrica / Hito del Embudo': '4. Formularios Completos (form_complete)', 'Valor': telemetry.form_complete ?? totalReg, 'Detalle': 'Registros exitosos con token OTT generado' },
                { 'Métrica / Hito del Embudo': '5. Partidas Iniciadas (game_start)', 'Valor': telemetry.game_start ?? 0, 'Detalle': 'Entradas a la cuenta regresiva' },
                { 'Métrica / Hito del Embudo': '6. Triggers Disparados (trigger_fired)', 'Valor': telemetry.trigger_fired ?? 0, 'Detalle': 'Señales sonoras y visuales emitidas' },
                { 'Métrica / Hito del Embudo': '7. Partidas Completadas (game_complete)', 'Valor': telemetry.game_complete ?? totalPlayed, 'Detalle': 'Jugadas finalizadas con tiempo procesado' },
                { 'Métrica / Hito del Embudo': '8. Mejor Récord Histórico', 'Valor': stats?.bestReactionTimeMs ? `${stats.bestReactionTimeMs} ms` : '—', 'Detalle': 'Mejor tiempo de reacción sin penalización' },
                { 'Métrica / Hito del Embudo': '9. Tiempo Promedio de Reacción', 'Valor': stats?.averageReactionTimeMs ? `${Math.round(stats.averageReactionTimeMs)} ms` : '—', 'Detalle': 'Media aritmética de participantes' },
                { 'Métrica / Hito del Embudo': '10. Tasa de Éxito / Conversión', 'Valor': convRate, 'Detalle': 'Porcentaje de participantes que completaron su intento' },
                { 'Métrica / Hito del Embudo': '', 'Valor': '', 'Detalle': '' },
                { 'Métrica / Hito del Embudo': '=== BALANCE DE DESCALIFICACIONES ===', 'Valor': '', 'Detalle': '' },
                { 'Métrica / Hito del Embudo': 'Total Descalificados / Salidas Falsas', 'Valor': stats?.totalDisqualified ?? 0, 'Detalle': 'Participantes con toques anticipados o fuera de umbral' }
            ];

            // --- HOJA 4: AUDITORÍA DE REINTENTOS ---
            const resetParticipants = participants.filter((p) => p.resetByAdmin || p.isArchived || p.previousReactionTimeMs !== undefined);
            const sheet4Data = resetParticipants.length > 0 ? resetParticipants.map((p, idx) => ({
                '#': idx + 1,
                'ID Participante': p.id || '—',
                'Nombre del Aficionado': p.participantName || '—',
                'Correo Electrónico': p.email || '—',
                'Teléfono': p.phone || '—',
                'Tiempo Anterior (ms)': p.previousReactionTimeMs || p.totalTimeMs || '—',
                'Fecha de Reactivación': p.resetAtUtc ? new Date(p.resetAtUtc).toLocaleString('es-MX') : '—',
                'Estado Actual': p.outcomeStatus || (p.playedAtUtc ? 'JUGÓ DE NUEVO' : 'PENDIENTE')
            })) : [{ 'Mensaje': 'No se han registrado reactivaciones individuales en esta sala.' }];

            // Crear libro de trabajo Excel
            const workbook = XLSX.utils.book_new();
            const ws1 = XLSX.utils.json_to_sheet(sheet1Data);
            const ws2 = XLSX.utils.json_to_sheet(sheet2Data);
            const ws3 = XLSX.utils.json_to_sheet(sheet3Data);
            const ws4 = XLSX.utils.json_to_sheet(sheet4Data);

            // Ajuste de anchos de columna
            ws1['!cols'] = [{ wch: 10 }, { wch: 28 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 25 }, { wch: 14 }, { wch: 22 }];
            ws2['!cols'] = [{ wch: 6 }, { wch: 36 }, { wch: 28 }, { wch: 28 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 25 }, { wch: 12 }, { wch: 22 }, { wch: 22 }, { wch: 20 }];
            ws3['!cols'] = [{ wch: 45 }, { wch: 25 }, { wch: 60 }];
            ws4['!cols'] = [{ wch: 6 }, { wch: 36 }, { wch: 28 }, { wch: 28 }, { wch: 18 }, { wch: 22 }, { wch: 22 }, { wch: 20 }];

            XLSX.utils.book_append_sheet(workbook, ws1, 'Ranking Oficial');
            XLSX.utils.book_append_sheet(workbook, ws2, 'Participantes');
            XLSX.utils.book_append_sheet(workbook, ws3, 'Telemetría y Conversión');
            XLSX.utils.book_append_sheet(workbook, ws4, 'Auditoría de Reintentos');

            const fileName = `Reporte_Reaccion_Rapida_Personal_${cleanRoom}_${todayStr}.xlsx`;
            XLSX.writeFile(workbook, fileName);

            triggerToast('success', `Reporte exportado exitosamente: ${fileName}`);
        } catch (error) {
            console.error('[QuickReactionPersonalBoard] Error al exportar a Excel:', error);
            triggerToast('danger', 'Error al generar el libro de Excel.');
        } finally {
            setIsExportingExcel(false);
        }
    };

    // Helper de tema actual para preview
    const selectedThemeObject = useMemo(() => {
        return themes.find((t) => t.id === selectedThemeId) || activeTheme || null;
    }, [themes, selectedThemeId, activeTheme]);

    return (
        <div className="QrpBoard">
            {/* TOAST / ALERTA FLOTANTE */}
            {alertNotification && (
                <div className={`QrpBoard__Toast QrpBoard__Toast--${alertNotification.type}`}>
                    <i className={`bi ${alertNotification.type === 'success' ? 'bi-check-circle-fill' : alertNotification.type === 'danger' ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'}`} />
                    <span>{alertNotification.message}</span>
                </div>
            )}

            {/* =========================================================================
                HEADER ADMINISTRATIVO PRINCIPAL
               ========================================================================= */}
            <header className="QrpBoard__Header">
                <div className="QrpBoard__HeaderLeft">
                    <button
                        type="button"
                        className="QrpBoard__BackBtn"
                        onClick={getOutOfTheDynamic}
                        title="Volver al Dashboard"
                    >
                        <i className="bi bi-arrow-left" />
                    </button>
                    <div className="QrpBoard__Branding">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-lightning-charge-fill QrpBoard__Icon text-warning" />
                            <h1 className="QrpBoard__Title">Reacción Rápida Personal</h1>
                            <span className="QrpBoard__Badge">HU-06 Admin</span>
                        </div>
                        <span className="QrpBoard__Subtitle">
                            Sala: <strong>{roomId || 'Sin Asignar'}</strong>
                        </span>
                    </div>
                </div>

                <div className="QrpBoard__HeaderRight">
                    {/* Badge de conexión SignalR */}
                    <div className={`QrpBoard__WsBadge QrpBoard__WsBadge--${wsStatus}`}>
                        <span className="QrpBoard__WsDot" />
                        <span>{wsStatus === 'connected' ? 'En Vivo' : wsStatus === 'connecting' ? 'Conectando...' : wsStatus === 'reconnecting' ? 'Reconectando...' : 'Desconectado'}</span>
                    </div>

                    {/* Botón de Refresco Manual */}
                    <button
                        type="button"
                        className="QrpBoard__RefreshBtn"
                        onClick={() => loadAdminData(false)}
                        disabled={isLoading}
                        title="Refrescar datos de la sala"
                    >
                        <i className={`bi bi-arrow-clockwise ${isLoading ? 'QrpBoard__Spinning' : ''}`} />
                        <span>Actualizar</span>
                    </button>
                </div>
            </header>

            {/* =========================================================================
                FILA DE 10 KPIS DE TELEMETRÍA (HORIZONTAL SCROLL TOUCH)
               ========================================================================= */}
            <section className="QrpBoard__MetricsSection">
                <QuickReactionPersonalMetricsRow stats={stats} />
            </section>

            {/* =========================================================================
                4 ACORDEONES DESPLEGABLES DE GESTIÓN Y CONTROL
               ========================================================================= */}
            <section className="QrpBoard__AccordionsSection">
                <div className="QrpBoard__AccordionsContainer">
                    {/* -------------------------------------------------------------
                        ACORDEÓN 1: CONTROL DE LIVE OVERLAY
                       ------------------------------------------------------------- */}
                    <div className={`QrpAccordion ${openAccordion === 'overlay' ? 'QrpAccordion--open' : ''}`}>
                        <div
                            className="QrpAccordion__Header"
                            onClick={() => toggleAccordion('overlay')}
                        >
                            <div className="QrpAccordion__HeaderTitle">
                                <i className="bi bi-broadcast text-danger" />
                                <span>1. Control de Live Overlay (Transmisión OBS / Cintillo)</span>
                            </div>
                            <div className="QrpAccordion__HeaderMeta">
                                <span className={`QrpBadge ${overlayVisible ? 'QrpBadge--success' : 'QrpBadge--muted'}`}>
                                    {overlayVisible ? 'Visible en Pantalla' : 'Oculto'}
                                </span>
                                <i className={`bi bi-chevron-${openAccordion === 'overlay' ? 'up' : 'down'}`} />
                            </div>
                        </div>

                        {openAccordion === 'overlay' && (
                            <div className="QrpAccordion__Body">
                                <form onSubmit={handleSaveOverlay}>
                                    <div className="row g-3">
                                        {/* Switch Visibilidad */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label">Visibilidad en Pantalla</label>
                                            <div className="QrpToggleWrapper">
                                                <button
                                                    type="button"
                                                    className={`QrpToggleBtn ${overlayVisible ? 'QrpToggleBtn--active' : ''}`}
                                                    onClick={() => setOverlayVisible(!overlayVisible)}
                                                >
                                                    <span className="QrpToggleBtn__Thumb" />
                                                </button>
                                                <span className="QrpToggleWrapper__Text">
                                                    {overlayVisible ? 'Overlay Activo (Visible)' : 'Overlay Apagado (Oculto)'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Modo de Overlay (invitation / winner) */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label">Modo del Overlay</label>
                                            <select
                                                className="QrpForm__Select"
                                                value={overlayMode}
                                                onChange={(e) => setOverlayMode(e.target.value)}
                                            >
                                                <option value="invitation">Invitación (Código QR + Llamado a la acción)</option>
                                                <option value="winner">Ganador Oficial (Récord del aficionado líder)</option>
                                            </select>
                                        </div>

                                        {/* Cantidad de QRs (1 QR / 2 QRs) */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label">Cantidad de Códigos QR</label>
                                            <div className="QrpSegmentedControl">
                                                <button
                                                    type="button"
                                                    className={`QrpSegmentedControl__Btn ${overlayQrCount === 1 ? 'QrpSegmentedControl__Btn--active' : ''}`}
                                                    onClick={() => setOverlayQrCount(1)}
                                                >
                                                    1 QR (Centro/Lateral)
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`QrpSegmentedControl__Btn ${overlayQrCount === 2 ? 'QrpSegmentedControl__Btn--active' : ''}`}
                                                    onClick={() => setOverlayQrCount(2)}
                                                >
                                                    2 QRs (Extremos)
                                                </button>
                                            </div>
                                        </div>

                                        {/* Slider Offset Y */}
                                        <div className="col-12 col-md-6">
                                            <label className="QrpForm__Label d-flex justify-content-between">
                                                <span>Ajuste Vertical Offset Y</span>
                                                <span className="text-warning font-bold">{overlayOffsetY} px</span>
                                            </label>
                                            <input
                                                type="range"
                                                className="QrpForm__Range"
                                                min="-200"
                                                max="200"
                                                step="5"
                                                value={overlayOffsetY}
                                                onChange={(e) => setOverlayOffsetY(Number(e.target.value))}
                                            />
                                        </div>

                                        {/* Dropzones de Cintillos 1536 y 1920 */}
                                        <div className="col-12 col-md-6">
                                            <label className="QrpForm__Label">Cintillos de Transmisión</label>
                                            <div className="d-flex gap-2 flex-wrap">
                                                {/* Cintillo 1536 */}
                                                <label className="QrpUploadDropzone flex-fill">
                                                    <i className="bi bi-aspect-ratio" />
                                                    <span>{overlayBanner1536Url ? 'Cintillo 1536px Asignado' : 'Subir Cintillo 1536x240'}</span>
                                                    {uploadingBannerType === 'banner_1536' && <span className="spinner-border spinner-border-sm ms-1" />}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        disabled={uploadingBannerType !== null}
                                                        onChange={(e) => {
                                                            const file = e.target.files && e.target.files[0];
                                                            if (file) handleUploadOverlayBanner(file, 'banner_1536');
                                                        }}
                                                    />
                                                </label>

                                                {/* Cintillo 1920 */}
                                                <label className="QrpUploadDropzone flex-fill">
                                                    <i className="bi bi-tv" />
                                                    <span>{overlayBanner1920Url ? 'Cintillo 1920px Asignado' : 'Subir Cintillo 1920x300'}</span>
                                                    {uploadingBannerType === 'banner_1920' && <span className="spinner-border spinner-border-sm ms-1" />}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        disabled={uploadingBannerType !== null}
                                                        onChange={(e) => {
                                                            const file = e.target.files && e.target.files[0];
                                                            if (file) handleUploadOverlayBanner(file, 'banner_1920');
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Botón Guardar Overlay */}
                                    <div className="mt-3 text-end">
                                        <button
                                            type="submit"
                                            className="QrpBtn QrpBtn--primary"
                                            disabled={isSavingOverlay}
                                        >
                                            {isSavingOverlay ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" />
                                                    <span>Guardando Overlay...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check2-circle me-2" />
                                                    <span>Actualizar Overlay</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>

                    {/* -------------------------------------------------------------
                        ACORDEÓN 2: PARÁMETROS DEL JUEGO Y ANTI-SPAM
                       ------------------------------------------------------------- */}
                    <div className={`QrpAccordion ${openAccordion === 'game' ? 'QrpAccordion--open' : ''}`}>
                        <div
                            className="QrpAccordion__Header"
                            onClick={() => toggleAccordion('game')}
                        >
                            <div className="QrpAccordion__HeaderTitle">
                                <i className="bi bi-sliders text-cyan" />
                                <span>2. Parámetros del Juego y Reglas Anti-Spam</span>
                            </div>
                            <div className="QrpAccordion__HeaderMeta">
                                <span className={`QrpBadge ${gameIsActive ? 'QrpBadge--success' : 'QrpBadge--danger'}`}>
                                    {gameIsActive ? 'Juego Activo' : 'Juego en Pausa'}
                                </span>
                                <i className={`bi bi-chevron-${openAccordion === 'game' ? 'up' : 'down'}`} />
                            </div>
                        </div>

                        {openAccordion === 'game' && (
                            <div className="QrpAccordion__Body">
                                <form onSubmit={handleSaveConfig}>
                                    <div className="row g-3">
                                        {/* Switch Activo / Pausa */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label">Estado de Participación</label>
                                            <div className="QrpToggleWrapper">
                                                <button
                                                    type="button"
                                                    className={`QrpToggleBtn ${gameIsActive ? 'QrpToggleBtn--active' : ''}`}
                                                    onClick={() => setGameIsActive(!gameIsActive)}
                                                >
                                                    <span className="QrpToggleBtn__Thumb" />
                                                </button>
                                                <span className="QrpToggleWrapper__Text">
                                                    {gameIsActive ? 'Permitiendo Participaciones' : 'Pausado (No acepta jugadas)'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Estrategia Anti-Spam */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label">Estrategia Ante Falta Temprana</label>
                                            <select
                                                className="QrpForm__Select"
                                                value={earlyFaultStrategy}
                                                onChange={(e) => setEarlyFaultStrategy(e.target.value)}
                                            >
                                                <option value="NONE">NONE (Sin penalización / libre)</option>
                                                <option value="THROTTLE">THROTTLE (Bloqueo temporal de pulsación)</option>
                                                <option value="RESET_DELAY">RESET_DELAY (Reinicia cuenta regresiva aleatoria)</option>
                                                <option value="TIME_PENALTY">TIME_PENALTY (Suma +1000ms al tiempo final)</option>
                                            </select>
                                        </div>

                                        {/* Modo Estricto / Falta Temprana */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label">Detección Estricta de Faltas</label>
                                            <div className="QrpToggleWrapper">
                                                <button
                                                    type="button"
                                                    className={`QrpToggleBtn ${allowEarlyFaultDetection ? 'QrpToggleBtn--active' : ''}`}
                                                    onClick={() => setAllowEarlyFaultDetection(!allowEarlyFaultDetection)}
                                                >
                                                    <span className="QrpToggleBtn__Thumb" />
                                                </button>
                                                <span className="QrpToggleWrapper__Text">
                                                    {allowEarlyFaultDetection ? 'Descalificar por Salida Falsa' : 'Permitir Reintento en Pantalla'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Slider Retardo Mínimo */}
                                        <div className="col-12 col-md-6">
                                            <label className="QrpForm__Label d-flex justify-content-between">
                                                <span>Retardo Mínimo de Espera</span>
                                                <span className="text-warning font-bold">{(minWaitDelayMs / 1000).toFixed(1)} s ({minWaitDelayMs} ms)</span>
                                            </label>
                                            <input
                                                type="range"
                                                className="QrpForm__Range"
                                                min="1000"
                                                max="10000"
                                                step="250"
                                                value={minWaitDelayMs}
                                                onChange={(e) => setMinWaitDelayMs(Number(e.target.value))}
                                            />
                                        </div>

                                        {/* Slider Retardo Máximo */}
                                        <div className="col-12 col-md-6">
                                            <label className="QrpForm__Label d-flex justify-content-between">
                                                <span>Retardo Máximo de Espera</span>
                                                <span className="text-warning font-bold">{(maxWaitDelayMs / 1000).toFixed(1)} s ({maxWaitDelayMs} ms)</span>
                                            </label>
                                            <input
                                                type="range"
                                                className="QrpForm__Range"
                                                min="2000"
                                                max="20000"
                                                step="250"
                                                value={maxWaitDelayMs}
                                                onChange={(e) => setMaxWaitDelayMs(Number(e.target.value))}
                                            />
                                        </div>

                                        {/* Slider Timeout Inactividad */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label d-flex justify-content-between">
                                                <span>Límite de Inactividad</span>
                                                <span className="text-cyan font-bold">{inactivityTimeoutSeconds} s</span>
                                            </label>
                                            <input
                                                type="range"
                                                className="QrpForm__Range"
                                                min="3"
                                                max="30"
                                                step="1"
                                                value={inactivityTimeoutSeconds}
                                                onChange={(e) => setInactivityTimeoutSeconds(Number(e.target.value))}
                                            />
                                        </div>

                                        {/* Slider Umbral Humano Mínimo */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label d-flex justify-content-between">
                                                <span>Umbral Fisiológico Humano</span>
                                                <span className="text-cyan font-bold">{minHumanReactionThresholdMs} ms</span>
                                            </label>
                                            <input
                                                type="range"
                                                className="QrpForm__Range"
                                                min="60"
                                                max="300"
                                                step="10"
                                                value={minHumanReactionThresholdMs}
                                                onChange={(e) => setMinHumanReactionThresholdMs(Number(e.target.value))}
                                            />
                                        </div>

                                        {/* Permitir Dispositivo Compartido */}
                                        <div className="col-12 col-md-4">
                                            <label className="QrpForm__Label">Dispositivo Compartido</label>
                                            <div className="QrpToggleWrapper">
                                                <button
                                                    type="button"
                                                    className={`QrpToggleBtn ${allowSharedDevice ? 'QrpToggleBtn--active' : ''}`}
                                                    onClick={() => setAllowSharedDevice(!allowSharedDevice)}
                                                >
                                                    <span className="QrpToggleBtn__Thumb" />
                                                </button>
                                                <span className="QrpToggleWrapper__Text">
                                                    {allowSharedDevice ? 'Permitir varios jugadores por móvil' : 'Bloquear múltiples registros'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Botón Guardar Parámetros */}
                                    <div className="mt-3 text-end">
                                        <button
                                            type="submit"
                                            className="QrpBtn QrpBtn--primary"
                                            disabled={isSavingConfig}
                                        >
                                            {isSavingConfig ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" />
                                                    <span>Guardando Reglas...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-shield-check me-2" />
                                                    <span>Guardar Parámetros</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>

                    {/* -------------------------------------------------------------
                        ACORDEÓN 3: GESTOR DE TEMAS VISUALES
                       ------------------------------------------------------------- */}
                    <div className={`QrpAccordion ${openAccordion === 'themes' ? 'QrpAccordion--open' : ''}`}>
                        <div
                            className="QrpAccordion__Header"
                            onClick={() => toggleAccordion('themes')}
                        >
                            <div className="QrpAccordion__HeaderTitle">
                                <i className="bi bi-palette-fill text-warning" />
                                <span>3. Gestor de Temas Visuales y Sprites</span>
                            </div>
                            <div className="QrpAccordion__HeaderMeta">
                                <span className="QrpBadge QrpBadge--gold">
                                    Tema Activo: {activeTheme?.name || 'Vaqueros del Oeste'}
                                </span>
                                <i className={`bi bi-chevron-${openAccordion === 'themes' ? 'up' : 'down'}`} />
                            </div>
                        </div>

                        {openAccordion === 'themes' && (
                            <div className="QrpAccordion__Body">
                                <div className="row g-3 align-items-center">
                                    {/* Selector de Tema */}
                                    <div className="col-12 col-md-5">
                                        <label className="QrpForm__Label">Seleccionar Tema de la Biblioteca</label>
                                        <select
                                            className="QrpForm__Select"
                                            value={selectedThemeId}
                                            onChange={(e) => setSelectedThemeId(e.target.value)}
                                        >
                                            {themes.length === 0 && <option value="">Tema por Defecto</option>}
                                            {themes.map((theme) => (
                                                <option key={theme.id} value={theme.id}>
                                                    {theme.name} {theme.id === activeTheme?.id ? '(⭐ Activo)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Botones de Acción de Temas */}
                                    <div className="col-12 col-md-7 d-flex gap-2 flex-wrap justify-content-md-end">
                                        <button
                                            type="button"
                                            className="QrpBtn QrpBtn--outline-gold"
                                            onClick={handleActivateTheme}
                                            disabled={isActivatingTheme || !selectedThemeId || selectedThemeId === activeTheme?.id}
                                        >
                                            {isActivatingTheme ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" />
                                                    <span>Activando...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-check-circle-fill me-2" />
                                                    <span>Poner como Activo</span>
                                                </>
                                            )}
                                        </button>

                                        <button
                                            type="button"
                                            className="QrpBtn QrpBtn--secondary"
                                            onClick={() => {
                                                setEditingTheme(selectedThemeObject);
                                                setShowThemeModal(true);
                                            }}
                                        >
                                            <i className="bi bi-pencil-square me-2" />
                                            <span>Editar Tema</span>
                                        </button>

                                        <button
                                            type="button"
                                            className="QrpBtn QrpBtn--primary"
                                            onClick={() => {
                                                setEditingTheme(null);
                                                setShowThemeModal(true);
                                            }}
                                        >
                                            <i className="bi bi-plus-lg me-2" />
                                            <span>Nuevo Tema</span>
                                        </button>
                                    </div>

                                    {/* Previsualización del Tema Seleccionado */}
                                    {selectedThemeObject && (
                                        <div className="col-12 mt-3">
                                            <div className="QrpThemePreviewBox">
                                                <div className="QrpThemePreviewBox__Header">
                                                    <span className="QrpThemePreviewBox__Title">
                                                        Previsualización: {selectedThemeObject.name}
                                                    </span>
                                                </div>
                                                <div className="QrpThemePreviewBox__SpritesRow">
                                                    {selectedThemeObject.backgroundImageUrl && (
                                                        <div className="QrpThemePreviewBox__Item">
                                                            <span className="QrpThemePreviewBox__ItemLabel">Fondo</span>
                                                            <img src={resolveAssetUrl(selectedThemeObject.backgroundImageUrl)} alt="Fondo" />
                                                        </div>
                                                    )}
                                                    {selectedThemeObject.logoImageUrl && (
                                                        <div className="QrpThemePreviewBox__Item">
                                                            <span className="QrpThemePreviewBox__ItemLabel">Logo</span>
                                                            <img src={resolveAssetUrl(selectedThemeObject.logoImageUrl)} alt="Logo" />
                                                        </div>
                                                    )}
                                                    {selectedThemeObject.spriteBackUrl && (
                                                        <div className="QrpThemePreviewBox__Item">
                                                            <span className="QrpThemePreviewBox__ItemLabel">Sprite Espalda</span>
                                                            <img src={resolveAssetUrl(selectedThemeObject.spriteBackUrl)} alt="Sprite Espalda" />
                                                        </div>
                                                    )}
                                                    {selectedThemeObject.spriteFrontUrl && (
                                                        <div className="QrpThemePreviewBox__Item">
                                                            <span className="QrpThemePreviewBox__ItemLabel">Sprite Frente</span>
                                                            <img src={resolveAssetUrl(selectedThemeObject.spriteFrontUrl)} alt="Sprite Frente" />
                                                        </div>
                                                    )}
                                                    {selectedThemeObject.spriteShootUrl && (
                                                        <div className="QrpThemePreviewBox__Item">
                                                            <span className="QrpThemePreviewBox__ItemLabel">Sprite Disparo</span>
                                                            <img src={resolveAssetUrl(selectedThemeObject.spriteShootUrl)} alt="Sprite Disparo" />
                                                        </div>
                                                    )}
                                                    {selectedThemeObject.spriteVictoryUrl && (
                                                        <div className="QrpThemePreviewBox__Item">
                                                            <span className="QrpThemePreviewBox__ItemLabel">Sprite Victoria</span>
                                                            <img src={resolveAssetUrl(selectedThemeObject.spriteVictoryUrl)} alt="Sprite Victoria" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* -------------------------------------------------------------
                        ACORDEÓN 4: ZONA PELIGROSA (REINICIO TOTAL MAESTRO)
                       ------------------------------------------------------------- */}
                    <div className={`QrpAccordion ${openAccordion === 'danger' ? 'QrpAccordion--open' : ''}`}>
                        <div
                            className="QrpAccordion__Header QrpAccordion__Header--danger"
                            onClick={() => toggleAccordion('danger')}
                        >
                            <div className="QrpAccordion__HeaderTitle">
                                <i className="bi bi-radioactive text-danger" />
                                <span>4. Zona Peligrosa (Reinicio Maestro de Sala)</span>
                            </div>
                            <div className="QrpAccordion__HeaderMeta">
                                <span className="QrpBadge QrpBadge--danger">Protegido por Clave</span>
                                <i className={`bi bi-chevron-${openAccordion === 'danger' ? 'up' : 'down'}`} />
                            </div>
                        </div>

                        {openAccordion === 'danger' && (
                            <div className="QrpAccordion__Body">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div>
                                        <h6 className="text-danger fw-bold mb-1">Borrado Completo de Dinámica</h6>
                                        <p className="text-secondary small mb-0">
                                            Elimina todos los participantes, clasificaciones, telemetría y resultados registrados en esta sala.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="QrpBtn QrpBtn--danger"
                                        onClick={() => setShowDangerModal(true)}
                                    >
                                        <i className="bi bi-trash-fill me-2" />
                                        <span>Abrir Reinicio Total</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* =========================================================================
                TABLA DE PARTICIPANTES Y RANKING CON BUSCADOR Y EXPORTACIÓN EXCEL
               ========================================================================= */}
            <section className="QrpBoard__TableSection">
                <div className="QrpBoard__TableContainer">
                    {/* Barra de Herramientas Superior */}
                    <div className="QrpTableToolbar">
                        {/* Buscador en tiempo real */}
                        <div className="QrpTableToolbar__Search">
                            <i className="bi bi-search" />
                            <input
                                type="text"
                                className="QrpTableToolbar__SearchInput"
                                placeholder="Buscar participante por nombre, email o teléfono..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="QrpTableToolbar__ClearBtn"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <i className="bi bi-x-circle-fill" />
                                </button>
                            )}
                        </div>

                        {/* Filtro por Estado */}
                        <div className="QrpTableToolbar__Filter">
                            <select
                                className="QrpForm__Select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="ALL">Todos los Estados ({participants.length})</option>
                                <option value="PLAYED">Jugados con Éxito</option>
                                <option value="WINNER">Ganadores</option>
                                <option value="FAULT">Salidas Falsas / Descalificados</option>
                                <option value="PENDING">Pendientes de Jugar</option>
                                <option value="RESET">Reactivados / Reintentos</option>
                            </select>
                        </div>

                        {/* Botón Ocultar/Mostrar PII */}
                        <button
                            type="button"
                            className={`QrpBtn ${showPII ? 'QrpBtn--active-pii' : 'QrpBtn--secondary'}`}
                            onClick={() => setShowPII(!showPII)}
                            title={showPII ? 'Ocultar datos sensibles (PII)' : 'Mostrar correos y teléfonos completos'}
                        >
                            <i className={`bi bi-eye${showPII ? '-slash' : ''}-fill me-2`} />
                            <span>{showPII ? 'Ocultar PII' : 'Mostrar PII'}</span>
                        </button>

                        {/* Botón Exportar a Excel */}
                        <button
                            type="button"
                            className="QrpBtn QrpBtn--excel"
                            onClick={handleExportExcel}
                            disabled={isExportingExcel || participants.length === 0}
                            title="Exportar libro de 4 hojas a Excel (.xlsx)"
                        >
                            {isExportingExcel ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    <span>Generando Excel...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-file-earmark-spreadsheet-fill me-2" />
                                    <span>Exportar Excel (4 Hojas)</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Tabla Responsiva */}
                    <div className="QrpTableWrapper">
                        <table className="QrpTable">
                            <thead>
                                <tr>
                                    <th style={{ width: '60px' }}>#</th>
                                    <th>Participante</th>
                                    <th>Correo</th>
                                    <th>Teléfono</th>
                                    <th>Tiempo (ms)</th>
                                    <th>Penalización</th>
                                    <th>Total (ms)</th>
                                    <th>Insignia</th>
                                    <th>Estado</th>
                                    <th style={{ width: '160px', textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredParticipants.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="QrpTable__Empty">
                                            <i className="bi bi-inbox fs-2 mb-2 d-block" />
                                            <span>No se encontraron participantes con los filtros actuales.</span>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredParticipants.map((p, index) => {
                                        const isFault = Boolean(p.isEarlyFault) || p.outcomeStatus === 'DISQUALIFIED';
                                        const totalMs = p.totalTimeMs || (p.reactionTimeMs ? (p.reactionTimeMs + (p.penaltyTimeMs || 0)) : null);

                                        return (
                                            <tr
                                                key={p.id || index}
                                                className={`QrpTable__Row ${p.isWinner ? 'QrpTable__Row--winner' : ''} ${isFault ? 'QrpTable__Row--fault' : ''}`}
                                            >
                                                <td className="font-bold text-secondary">
                                                    {p.rankingPosition || index + 1}
                                                </td>
                                                <td>
                                                    <div className="QrpTable__ParticipantCell">
                                                        <span className="QrpTable__ParticipantName">
                                                            {p.participantName || 'Sin Nombre'}
                                                        </span>
                                                        {p.isWinner && (
                                                            <span className="QrpBadge QrpBadge--gold ms-2">
                                                                👑 Ganador
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="QrpTable__PiiCell">
                                                    {maskEmail(p.email)}
                                                </td>
                                                <td className="QrpTable__PiiCell">
                                                    {maskPhone(p.phone)}
                                                </td>
                                                <td>
                                                    {p.reactionTimeMs ? (
                                                        <span className="font-mono text-warning">
                                                            {p.reactionTimeMs} ms
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {p.penaltyTimeMs && p.penaltyTimeMs > 0 ? (
                                                        <span className="font-mono text-danger">
                                                            +{p.penaltyTimeMs} ms
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">0 ms</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {totalMs ? (
                                                        <span className="font-mono font-bold text-cyan">
                                                            {totalMs} ms
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {p.badgeName ? (
                                                        <span className="QrpBadge QrpBadge--badge">
                                                            {p.badgeName}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {isFault ? (
                                                        <span className="QrpPill QrpPill--fault">
                                                            Salida Falsa
                                                        </span>
                                                    ) : p.playedAtUtc ? (
                                                        <span className="QrpPill QrpPill--played">
                                                            Jugado
                                                        </span>
                                                    ) : p.resetByAdmin ? (
                                                        <span className="QrpPill QrpPill--reset">
                                                            Reactivado
                                                        </span>
                                                    ) : (
                                                        <span className="QrpPill QrpPill--pending">
                                                            Pendiente
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        className="QrpBtn QrpBtn--action-reset"
                                                        title="Dar otra oportunidad al aficionado"
                                                        onClick={() => {
                                                            setResetTargetParticipant(p);
                                                            setShowResetModal(true);
                                                        }}
                                                    >
                                                        <i className="bi bi-arrow-repeat me-1" />
                                                        <span>Reintentar</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* =========================================================================
                MODALES TÁCTILES
               ========================================================================= */}
            {/* Modal de Temas */}
            <QuickReactionPersonalThemeModal
                isOpen={showThemeModal}
                onClose={() => setShowThemeModal(false)}
                roomId={roomId}
                initialTheme={editingTheme}
                onSaved={() => {
                    GetThemes(roomId).then((res) => {
                        if (res && res.success && Array.isArray(res.data)) {
                            setThemes(res.data);
                        }
                    }).catch(() => {});
                    loadAdminData(true);
                }}
            />

            {/* Modal de Reintento / Dar Otra Oportunidad */}
            <QuickReactionPersonalResetModal
                isOpen={showResetModal}
                onClose={() => setShowResetModal(false)}
                roomId={roomId}
                participant={resetTargetParticipant}
                onSuccess={(participant) => {
                    triggerToast('success', `Segunda oportunidad otorgada a ${participant?.participantName || 'el aficionado'}.`);
                    loadAdminData(true);
                }}
            />

            {/* Modal de Zona Peligrosa */}
            <QuickReactionPersonalDangerModal
                isOpen={showDangerModal}
                onClose={() => setShowDangerModal(false)}
                roomId={roomId}
                onSuccess={() => {
                    triggerToast('warning', 'La dinámica ha sido reiniciada por completo.');
                    loadAdminData(true);
                }}
            />
        </div>
    );
};

QuickReactionPersonalBoard.propTypes = {
    getOutOfTheDynamic: PropTypes.func,
    roomId: PropTypes.string
};

export default QuickReactionPersonalBoard;
